// =================================================================
// Engine: Special populations (PSD §4.2 / TODO Task 3.3)
// Peritoneal dialysis DEFERRED (Q3) — documented as known limitation.
// =================================================================
import { ConfidenceStatus } from '@amino-pk/shared-types';
import { ok } from './types';
/** Hemodialysis: loading + post-HD maintenance + predialysis thresholds. */
export function computeHemodialysisRegimen(params, dosingWeightKg) {
    const load = params.eiDoseMgKg * dosingWeightKg;
    const maint = load * 0.5;
    return ok({
        doseMg: maint,
        intervalHr: 48,
        pathway: 'hemodialysis',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: `Loading ${Math.round(load)} mg, then ${Math.round(maint)} mg after each HD session. Check predialysis trough.`,
    });
}
/** CRRT-specific pathway. */
export function computeCRRTRegimen(params, dosingWeightKg) {
    const dose = params.eiDoseMgKg * dosingWeightKg;
    return ok({
        doseMg: dose,
        intervalHr: 24,
        pathway: 'crrt',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: 'CRRT — monitor post-load level early; clearance depends on effluent rate.',
    });
}
/** Peritoneal dialysis — DEFERRED (Q3 resolved: ship without PD). */
export function computePeritonealDialysisRegimen() {
    return ok({
        doseMg: 0,
        intervalHr: 0,
        pathway: 'peritoneal-dialysis',
        confidence: ConfidenceStatus.OutOfRange,
        advisory: 'Peritoneal dialysis pathway is a known limitation (deferred). Use clinical judgment.',
    });
}
/** Obesity-corrected Vd + early confirmation. */
export function computeObesityVd(baseVdLkg) {
    return { vdLkg: baseVdLkg * 1.1, note: 'Obesity: Vd adjusted (+10%); recommend early peak confirmation.' };
}
/** Elderly conservative defaults. */
export function computeElderlyConservative(params, dosingWeightKg) {
    const dose = params.eiDoseMinMgKg * dosingWeightKg;
    return ok({
        doseMg: dose,
        intervalHr: 36,
        pathway: 'elderly-high-risk',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: 'Elderly/high-risk: conservative dosing; monitor renal function closely.',
    });
}
/** Augmented renal clearance detection. */
export function detectAugmentedRenalClearance(crClMlMin) {
    const arc = crClMlMin >= 130;
    return {
        arc,
        recommendation: arc
            ? 'ARC detected (CrCl ≥ 130) — consider q12h pathway; risk of subtherapeutic peaks.'
            : 'No ARC.',
    };
}
/** Pediatric age-banded dosing (excludes ECMO). */
export function computePediatricDose(params, weightKg, ageYears) {
    if (ageYears >= 18) {
        return ok({
            doseMg: 0,
            intervalHr: 0,
            pathway: 'pediatric',
            confidence: ConfidenceStatus.OutOfRange,
            advisory: 'Patient is adult — use standard pathway.',
        });
    }
    const dose = params.eiDoseMgKg * weightKg;
    return ok({
        doseMg: dose,
        intervalHr: 24,
        pathway: 'pediatric',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: 'Pediatric age-banded dosing (ECMO excluded). Verify with pediatric protocol.',
    });
}
/** Cystic fibrosis: increased Vd + higher dose range. */
export function computeCFParams(baseVdLkg, params, dosingWeightKg) {
    return {
        vdLkg: baseVdLkg * 1.2,
        doseMg: params.eiDoseMaxMgKg * 1.1 * dosingWeightKg,
    };
}
/** Pregnancy: disables EI, routes to traditional + fetal-risk advisory. */
export function computePregnancyRegimen(params, dosingWeightKg) {
    const dose = params.traditionalDoseMgKg * dosingWeightKg;
    return ok({
        doseMg: dose,
        intervalHr: 12,
        pathway: 'pregnancy',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: 'Pregnancy: extended-interval disabled; traditional divided dosing. Fetal-risk advisory — aminoglycosides carry fetal harm risk.',
    });
}
/** Burns/ascites: disables EI, larger initial dose, early peak/trough. */
export function computeBurnsAscitesRegimen(params, dosingWeightKg) {
    const dose = params.eiDoseMaxMgKg * 1.1 * dosingWeightKg;
    return ok({
        doseMg: dose,
        intervalHr: 12,
        pathway: 'burns-ascites',
        confidence: ConfidenceStatus.RequiresReview,
        advisory: 'Burns/ascites: extended-interval disabled; larger initial dose with early peak/trough.',
    });
}
//# sourceMappingURL=specialPopulations.js.map